import React from 'react';
import {
  DocumentRefitOutcome,
  type CaraWorkspaceArtifactFieldsFragment,
  type DocumentDesignFitState
} from '~/api/graphql';
import { ensureCaraWorkspaceArtifactFitsDesign } from '~/api/data/workspaces/mutations';

// Every fit the server finishes is published with its result. Only one that ends unrecorded, on
// a crash or a failed last attempt, says nothing; it is failed once the activity's seven-minute
// schedule budget and a minute of slack have passed.
export const FIT_DEADLINE_MS = 8 * 60_000;

type Fit = Pick<DocumentDesignFitState, 'needsRefit' | 'refused' | 'layoutKey' | 'appearanceKey'>;
type Status = {
  kind: 'idle' | 'updating' | 'pending' | 'failed';
  key: string;
  since?: Fit;
  /**
   * The fit wrote a later version the editor is moving to. Until it shows it, the version asked
   * about stays read-only: an edit typed there would only be refused as made on an old version,
   * and lost when the editor moves.
   */
  moving?: boolean;
};

/**
 * Keeps the open document fitted to its Design, from the fit state its host reads for the shown
 * version. It asks once per version and layout while the tab is visible and nothing is unsaved,
 * supersedes a fit it asked for under an older layout, and forces one only from Retry. A pending
 * or failed fit settles on the first state read after the answer.
 */
export function useAutomaticDesignRefit(props: {
  artifactId: string;
  version: number;
  enabled: boolean;
  unsaved: boolean;
  /** The shown version's fit state. A newer object is a newer read. */
  fitState: Fit | null | undefined;
  onRefitted: (artifact: CaraWorkspaceArtifactFieldsFragment) => void;
}) {
  const { artifactId, version, enabled, unsaved } = props;
  const fit = enabled ? (props.fitState ?? undefined) : undefined;
  const key = `${artifactId}:${version}`;
  const [visible, setVisible] = React.useState(() => document.visibilityState === 'visible');
  const [status, setStatus] = React.useState<Status>({ kind: 'idle', key });
  // A status about one version says nothing about the next, but a request still out keeps the
  // editor read-only until it answers.
  const shown = status.moving
    ? status.key === key
      ? 'updating'
      : 'idle'
    : status.key === key || status.kind === 'updating'
      ? status.kind
      : 'idle';
  const [renderRevision, setRenderRevision] = React.useState(0);
  const callbacks = React.useRef(props);
  callbacks.current = props;
  const shownRef = React.useRef({ key, version, fit });
  shownRef.current = { key, version, fit };
  // The version and layout last asked about, so a repeated read asks nothing and a layout saved
  // after the ask supersedes it. StrictMode runs effects twice; this keeps it to one request.
  const asked = React.useRef<{ key: string; layoutKey: string }>();
  // Not abort: the server keeps fitting for whoever opens the document next.
  const mounted = React.useRef(false);
  React.useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  React.useEffect(() => {
    const onVisibility = () => setVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Any Design save changes the appearance key, and only then does the preview re-render.
  const appearance = React.useRef<{ artifactId: string; key: string }>();
  React.useEffect(() => {
    if (!fit) return;
    const before = appearance.current;
    appearance.current = { artifactId, key: fit.appearanceKey };
    if (before?.artifactId === artifactId && before.key !== fit.appearanceKey)
      setRenderRevision((n) => n + 1);
  }, [fit, artifactId]);

  const ask = React.useCallback(
    async (supersede: boolean) => {
      // A read delivered while the request is out may already say how the fit ended.
      const { key: askedKey, version: askedVersion, fit: since } = shownRef.current;
      setStatus({ kind: 'updating', key: askedKey });
      const { data: result, error } = await ensureCaraWorkspaceArtifactFitsDesign({
        artifactId,
        supersede
      }).catch((err: unknown) => ({ data: undefined, error: err }));
      // The editor that asked is gone, and so is the document it was showing.
      if (!mounted.current) return;
      const refit = result?.ensureCaraWorkspaceArtifactFitsDesign;
      // Any answer naming a later version is followed, not only REFITTED: a fit saved before its
      // workflow crashed is answered UNCHANGED by the retry, with the version it wrote.
      if (
        refit &&
        (refit.outcome === DocumentRefitOutcome.Refitted || refit.artifact.version > askedVersion)
      ) {
        const follow = shownRef.current.key === askedKey;
        setStatus({ kind: follow ? 'updating' : 'idle', key: askedKey, moving: follow });
        if (follow) callbacks.current.onRefitted(refit.artifact);
        return;
      }
      const kind =
        error || refit?.outcome === DocumentRefitOutcome.RefitFailed
          ? 'failed'
          : refit?.outcome === DocumentRefitOutcome.RefitPending
            ? 'pending'
            : 'idle';
      setStatus({ kind, key: askedKey, since });
    },
    [artifactId]
  );

  const busy = shown === 'updating';
  React.useEffect(() => {
    if (!fit?.needsRefit || unsaved || !visible || busy) return;
    const before = asked.current;
    if (before?.key === key && before.layoutKey === fit.layoutKey) return;
    asked.current = { key, layoutKey: fit.layoutKey };
    void ask(before?.key === key);
  }, [fit, unsaved, visible, busy, key, ask]);

  // Once the editor has moved on, the version it left is an old one like any other, opened from
  // the version picker or not. A move that never arrives is let go after the fit's deadline.
  React.useEffect(() => {
    if (!status.moving) return;
    if (status.key !== key) {
      setStatus({ kind: 'idle', key });
      return;
    }
    const timer = setTimeout(
      () => setStatus((now) => (now.moving ? { kind: 'idle', key: now.key } : now)),
      FIT_DEADLINE_MS
    );
    return () => clearTimeout(timer);
  }, [status, key]);

  const pending = enabled && shown === 'pending';
  React.useEffect(() => {
    if (!pending) return;
    const timer = setTimeout(
      () => setStatus((now) => (now.kind === 'pending' ? { ...now, kind: 'failed' } : now)),
      FIT_DEADLINE_MS
    );
    return () => clearTimeout(timer);
  }, [pending]);

  // The read the request was sent against says nothing about how the fit ended; any later one
  // does. A refused fit is recorded, so the document stops reading as stale, and ends as a failure.
  // A forced Retry of a document that was not stale cannot be told from an unrelated read, so it
  // settles on the next one; a version it writes later still arrives on the feed.
  React.useEffect(() => {
    if ((shown !== 'pending' && shown !== 'failed') || !fit || fit === status.since) return;
    if (fit.needsRefit) return;
    const kind = fit.refused ? 'failed' : 'idle';
    setStatus((now) => (now.kind === kind ? now : { kind, key }));
  }, [shown, fit, status.since, key]);

  const retry = React.useCallback(() => {
    const { key: shownKey, fit: shownFit } = shownRef.current;
    if (shownFit) asked.current = { key: shownKey, layoutKey: shownFit.layoutKey };
    void ask(true);
  }, [ask]);

  return {
    renderRevision,
    updating: busy,
    pending,
    failed: enabled && shown === 'failed',
    retry
  };
}
